import { TranslateService } from '@ngx-translate/core';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ODataArray, ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { ArrayModel, JsonArrayModel } from '../complex/array.model';
import { CompositeModel, ODataCompositeOutModel } from '../complex/composite.model';
import { ExpenseModel, ExpenseModelFromJson, JsonExpenseModel } from '../low-level/expense.model';
import {
    JsonNotificationIdentifierModel,
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
} from '../low-level/notification-identifier.model';
import { TextModelOptions } from '../options/text-model-options';
import { BooleanModel } from '../primitive/boolean.model';
import { TextModel } from '../primitive/text.model';
import { UserMasterInstanceToken } from '../../interfaces/user-master.interface';
import { TimesheetService } from '../../services/timesheet/timesheet.service';
import { ConfigInstanceToken } from '../../interfaces/config.interface';

export class ConfirmationExpensesModel extends CompositeModel {
    public distance: TextModel;
    public expenses: ArrayModel<ExpenseModel>;
    public additionalTravelExpenses: DisplayDecorator<BooleanModel>;
    public displayDistance: boolean;
    public displayExpenses: boolean;
    public displayAdditionalTravelCost: boolean;
    public constructor(
        public notificationId: NotificationIdentifierModel,
        public isRemoteWork = false,
        distance?: string,
        public isDistanceReadonly?: boolean,
    ) {
        super('ConfirmationExpensesModel');

        const injector = CustomInjector.getInjector();
        const translate = injector.get<TranslateService>(TranslateService);
        const modelOptionsLoader = injector.get<ModelOptionsLoaderService>(ModelOptionsLoaderService);
        const configService = injector.get(ConfigInstanceToken);
        const userMasterService = injector.get(UserMasterInstanceToken);
        const timesheetService = injector.get<TimesheetService>(TimesheetService);

        this.displayDistance = configService.getDisplayConfirmationDistance() && !this.isRemoteWork;
        this.displayExpenses =
            !userMasterService.isPaceOnlineUser() &&
            !this.isRemoteWork &&
            timesheetService.isTimesheetFeatureEnabled() &&
            configService.getDisplayConfirmationExpenses();
        this.displayAdditionalTravelCost = configService.getDisplayAdditionalTravelCost() && !this.isRemoteWork;

        if (this.displayDistance) {
            this.distance = <TextModel>this.set(
                new TextModel(
                    'KiloMeter',
                    distance,
                    new TextModelOptions({
                        unit: <string>translate.instant('CONFIRMATION_EXPENSES_KM_LABEL'),
                        inputType: ConfigConstants.inputTypeNumeric,
                    }),
                ),
            );
            modelOptionsLoader.load(this.distance, 'ConfirmationExpense', 'Distance');
            if (typeof isDistanceReadonly !== 'undefined') {
                this.distance.options = {
                    ...this.distance.options,
                    readonly: isDistanceReadonly,
                };
            }
        }
        if (this.displayExpenses) {
            this.expenses = new ArrayModel<ExpenseModel>('CseExpenseSet');
        }
        if (this.displayAdditionalTravelCost) {
            const additionalTravelExpenses = new BooleanModel('AdditionalTravelExpenses');
            this.additionalTravelExpenses = new DisplayDecorator<BooleanModel>('', additionalTravelExpenses);
        }
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     *
     * @returns {any}
     */
    public toJSON(): JsonConfirmationExpensesModel {
        return {
            notificationId: this.notificationId,
            distance: this.distance,
            expenses: this.expenses,
            additionalTravelExpenses: this.displayAdditionalTravelCost
                ? this.additionalTravelExpenses?.model.value
                : false,
        };
    }

    public getOutModel(): ODataCompositeOutModel {
        const outModel = <ODataConfirmationExpensesOutModel>super.getOutModel();

        if (this.additionalTravelExpenses?.model.value) {
            outModel.CrmLongTextTRIDSet = [
                {
                    TaskNo: this.notificationId.maNum,
                    Notification: this.notificationId.qmNum,
                    TextID: 'TRID',
                    TextLine: 'X',
                },
            ];
        }
        return outModel;
    }
}

export class ConfirmationExpensesModelFromJson extends ConfirmationExpensesModel {
    public constructor(data: JsonConfirmationExpensesModel, isNotificationRemoteWork: boolean) {
        super(
            new NotificationIdentifierModelFromJson(data.notificationId),
            isNotificationRemoteWork,
            data.distance ? data.distance.value : undefined,
            data.distance?.options.readonly,
        );
        if (this.displayExpenses) {
            const expenses = data.expenses.value.map((expense: JsonExpenseModel) => {
                return new ExpenseModelFromJson(expense);
            });
            this.expenses.addModels(expenses);
        }
        if (this.displayAdditionalTravelCost) {
            this.additionalTravelExpenses.model.value = data.additionalTravelExpenses;
        }
    }
}

export interface JsonConfirmationExpensesModel {
    notificationId: JsonNotificationIdentifierModel;
    distance: TextModel;
    expenses: JsonArrayModel;
    additionalTravelExpenses: boolean;
}

export interface ODataConfirmationExpensesOutModel extends ODataObject {
    KiloMeter: string;
    CseExpenseSet: ODataArray;
    CrmLongTextTRIDSet: ODataAdditionalInfoExpensesOutModel[];
}

export interface ODataAdditionalInfoExpensesOutModel extends ODataObject {
    Notification: string;
    TaskNo: string;
    TextID: string;
    TextLine: string;
}
